import { WizardType } from '../wizards/WizardTypes';
import { SpellType } from './CooldownManager';

export type GameScreen = 'loading' | 'menu' | 'wizard-select' | 'playing' | 'game-over';

export type ComboId = 'inferno' | 'steamCloud' | 'shieldCounter';

export interface ComboResult {
  id: ComboId;
  name: string;
  damageMultiplier: number;
  description: string;
}

// Recognized spell combos
const COMBO_DEFINITIONS: { spells: SpellType[]; result: ComboResult }[] = [
  {
    spells: ['fireball', 'fireball'],
    result: { id: 'inferno', name: 'Inferno', damageMultiplier: 1.5, description: 'Double fire = larger explosion' }
  },
  {
    spells: ['iceShard', 'fireball'],
    result: { id: 'steamCloud', name: 'Steam Cloud', damageMultiplier: 1.3, description: 'Ice + Fire = steam AOE' }
  },
  {
    spells: ['shield', 'fireball'],
    result: { id: 'shieldCounter', name: 'Counter Strike', damageMultiplier: 1.4, description: 'Shield then counter = bonus damage' }
  },
  {
    spells: ['shield', 'iceShard'],
    result: { id: 'shieldCounter', name: 'Counter Strike', damageMultiplier: 1.4, description: 'Shield then counter = bonus damage' }
  },
];

export interface ComboState {
  meter: number;         // 0-100 combo meter, fills on hits, decays over time
  lastSpells: SpellType[]; // Recent spell history for combo detection
  activeCombo: ComboResult | null;
  comboCount: number;    // Consecutive successful combos
}

export interface PlayerState {
  health: number;
  maxHealth: number;
  wizardType: WizardType;
}

export interface MatchStats {
  spellsCast: Set<SpellType>;
  totalDamageTaken: number;
  totalDamageDealt: number;
  shieldsBlocked: number;
  combosTrigered: number;
}

export interface GameStateData {
  screen: GameScreen;
  player: PlayerState;
  enemy: PlayerState;
  winner: 'player' | 'enemy' | null;
  isPaused: boolean;
  playerCombo: ComboState;
  enemyCombo: ComboState;
}

export class GameState {
  private state: GameStateData;
  private listeners: Set<(state: GameStateData) => void> = new Set();
  private static readonly COMBO_WINDOW_MS = 4000; // Spells must be cast within 4s to combo
  private static readonly COMBO_DECAY_RATE = 5; // Meter points lost per second
  private playerSpellTimestamps: number[] = [];
  private enemySpellTimestamps: number[] = [];
  private _matchStats: MatchStats = this.getInitialMatchStats();

  constructor() {
    this.state = this.getInitialState();
  }

  private getInitialMatchStats(): MatchStats {
    return {
      spellsCast: new Set<SpellType>(),
      totalDamageTaken: 0,
      totalDamageDealt: 0,
      shieldsBlocked: 0,
      combosTrigered: 0,
    };
  }

  getMatchStats(): MatchStats {
    return this._matchStats;
  }

  recordMatchSpellCast(spell: SpellType): void {
    this._matchStats.spellsCast.add(spell);
  }

  recordMatchDamageTaken(amount: number): void {
    this._matchStats.totalDamageTaken += amount;
  }

  recordMatchDamageDealt(amount: number): void {
    this._matchStats.totalDamageDealt += amount;
  }

  recordMatchShieldBlock(): void {
    this._matchStats.shieldsBlocked++;
  }

  recordMatchCombo(): void {
    this._matchStats.combosTrigered++;
  }

  private getInitialComboState(): ComboState {
    return { meter: 0, lastSpells: [], activeCombo: null, comboCount: 0 };
  }

  private getInitialState(): GameStateData {
    return {
      screen: 'loading',
      player: {
        health: 100,
        maxHealth: 100,
        wizardType: 'archmage',
      },
      enemy: {
        health: 100,
        maxHealth: 100,
        wizardType: 'archmage',
      },
      winner: null,
      isPaused: false,
      playerCombo: this.getInitialComboState(),
      enemyCombo: this.getInitialComboState()
    };
  }

  getState(): GameStateData {
    return { ...this.state };
  }

  subscribe(listener: (state: GameStateData) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify(): void {
    this.listeners.forEach(listener => listener(this.getState()));
  }

  setScreen(screen: GameScreen): void {
    this.state.screen = screen;
    this.notify();
  }

  setPlayerWizard(type: WizardType): void {
    const stats = this.getWizardStats(type);
    this.state.player.wizardType = type;
    this.state.player.maxHealth = stats.health;
    this.state.player.health = stats.health;
    this.notify();
  }

  setEnemyWizard(type: WizardType): void {
    const stats = this.getWizardStats(type);
    this.state.enemy.wizardType = type;
    this.state.enemy.maxHealth = stats.health;
    this.state.enemy.health = stats.health;
    this.notify();
  }

  private getWizardStats(type: WizardType): { health: number } {
    switch (type) {
      case 'battlemage':
        return { health: 130 };
      case 'pyromancer':
        return { health: 90 };
      case 'frostmage':
        return { health: 100 };
      case 'necromancer':
        return { health: 95 };
      case 'druid':
        return { health: 110 };
      case 'chronomancer':
        return { health: 90 };
      case 'archmage':
      default:
        return { health: 100 };
    }
  }

  damagePlayer(amount: number): void {
    this.state.player.health = Math.max(0, this.state.player.health - amount);
    this.checkGameOver();
    this.notify();
  }

  damageEnemy(amount: number): void {
    this.state.enemy.health = Math.max(0, this.state.enemy.health - amount);
    this.checkGameOver();
    this.notify();
  }

  // Combo system methods
  recordSpellCast(isPlayer: boolean, spell: SpellType): ComboResult | null {
    const combo = isPlayer ? this.state.playerCombo : this.state.enemyCombo;
    const timestamps = isPlayer ? this.playerSpellTimestamps : this.enemySpellTimestamps;
    const now = Date.now();

    // Add spell to history
    combo.lastSpells.push(spell);
    timestamps.push(now);

    // Trim to last 3 spells and remove stale entries
    while (combo.lastSpells.length > 3) {
      combo.lastSpells.shift();
      timestamps.shift();
    }

    // Remove spells outside the combo window
    while (timestamps.length > 0 && now - timestamps[0] > GameState.COMBO_WINDOW_MS) {
      timestamps.shift();
      combo.lastSpells.shift();
    }

    // Check for combo matches (check 2-spell sequences)
    if (combo.lastSpells.length >= 2) {
      const lastTwo = combo.lastSpells.slice(-2);
      for (const def of COMBO_DEFINITIONS) {
        if (def.spells[0] === lastTwo[0] && def.spells[1] === lastTwo[1]) {
          combo.activeCombo = def.result;
          combo.comboCount++;
          combo.meter = Math.min(100, combo.meter + 25);
          this.notify();
          return def.result;
        }
      }
    }

    combo.activeCombo = null;
    this.notify();
    return null;
  }

  recordHit(isPlayer: boolean): void {
    const combo = isPlayer ? this.state.playerCombo : this.state.enemyCombo;
    combo.meter = Math.min(100, combo.meter + 15);
    this.notify();
  }

  recordMiss(isPlayer: boolean): void {
    const combo = isPlayer ? this.state.playerCombo : this.state.enemyCombo;
    combo.meter = Math.max(0, combo.meter - 10);
    combo.comboCount = 0;
    this.notify();
  }

  updateComboDecay(deltaSec: number): void {
    let changed = false;
    for (const combo of [this.state.playerCombo, this.state.enemyCombo]) {
      if (combo.meter > 0) {
        combo.meter = Math.max(0, combo.meter - GameState.COMBO_DECAY_RATE * deltaSec);
        changed = true;
      }
    }
    if (changed) {
      this.notify();
    }
  }

  getComboMultiplier(isPlayer: boolean): number {
    const combo = isPlayer ? this.state.playerCombo : this.state.enemyCombo;
    if (combo.activeCombo) {
      return combo.activeCombo.damageMultiplier;
    }
    // Combo meter provides a small bonus when high
    if (combo.meter >= 75) return 1.15;
    if (combo.meter >= 50) return 1.1;
    return 1.0;
  }

  private checkGameOver(): void {
    if (this.state.player.health <= 0) {
      this.state.winner = 'enemy';
      this.state.screen = 'game-over';
    } else if (this.state.enemy.health <= 0) {
      this.state.winner = 'player';
      this.state.screen = 'game-over';
    }
  }

  resetGame(): void {
    const playerType = this.state.player.wizardType;
    this.state = this.getInitialState();
    this.playerSpellTimestamps = [];
    this.enemySpellTimestamps = [];
    this._matchStats = this.getInitialMatchStats();
    this.setPlayerWizard(playerType);
    this.setEnemyWizard(this.getRandomEnemyType());
    this.state.screen = 'playing';
    this.notify();
  }

  private getRandomEnemyType(): WizardType {
    const types: WizardType[] = ['pyromancer', 'frostmage', 'archmage', 'battlemage', 'necromancer', 'druid', 'chronomancer'];
    return types[Math.floor(Math.random() * types.length)];
  }

  startGame(): void {
    this._matchStats = this.getInitialMatchStats();
    this.setEnemyWizard(this.getRandomEnemyType());
    this.state.screen = 'playing';
    this.notify();
  }

  // Multiplayer methods - set health directly from server
  setPlayerHealth(health: number): void {
    this.state.player.health = Math.max(0, health);
    this.checkGameOver();
    this.notify();
  }

  setEnemyHealth(health: number): void {
    this.state.enemy.health = Math.max(0, health);
    this.checkGameOver();
    this.notify();
  }

  endGame(winner: 'player' | 'enemy'): void {
    this.state.winner = winner;
    this.state.screen = 'game-over';
    this.notify();
  }
}
